/**
 * Branded logo tiles for each integration card.
 *
 * EROAD + Blackhawk: locally bundled webp assets imported from
 * `client/src/assets/integrations/`. Webpack fingerprints + serves them
 * via the `asset/resource` rule (webpack.config.ts), so cache-busting
 * and CDN safety come for free. To swap a logo, replace the file in
 * that folder (any extension webpack knows about — png/jpg/svg/webp).
 *
 * Carjam (NZTA) has no public brand asset we can bundle, so we render a
 * coloured monogram per the operator's spec — Carjam blue + the CARJAM
 * wordmark, kept consistent with the surrounding card aesthetic.
 */
import React from 'react';
import { cn } from '@/lib/utils';
import eroadLogo from '@/assets/integrations/eroad-logo-dark.webp';
import blackhawkLogo from '@/assets/integrations/BH-Logo.webp';

export interface LogoProps {
  className?: string;
}

/** Image-backed logo. White tile so transparent / dark-variant brand
 *  assets read consistently on either theme; `object-contain` preserves
 *  the source aspect ratio instead of squashing wide wordmarks. */
function ImageLogo({
  className, src, alt,
}: LogoProps & { src: string; alt: string }) {
  return (
    <span className={cn(
      'inline-flex items-center justify-center rounded-md bg-white border shrink-0 overflow-hidden p-1',
      className,
    )}>
      <img
        src={src}
        alt={alt}
        className="max-h-full max-w-full object-contain"
        // Defensive load behaviour: don't block first paint while the
        // logo round-trips to the third-party host.
        loading="lazy"
        // Stop the browser leaking the referring page when fetching a
        // third-party asset that isn't strictly necessary for the page.
        referrerPolicy="no-referrer"
      />
    </span>
  );
}

/** Wordmark monogram — coloured tile + bold caps in white. Used for
 *  integrations without a downloadable brand asset (e.g. Carjam). */
function Monogram({
  className, bg, text, label,
}: LogoProps & { bg: string; text: string; label: string }) {
  return (
    <span
      role="img"
      aria-label={label}
      className={cn(
        'inline-flex items-center justify-center rounded-md font-bold tracking-tight text-white shrink-0 select-none',
        bg,
        className,
      )}
    >
      <span className="text-[10px]">{text}</span>
    </span>
  );
}

export function EroadLogo(props: LogoProps) {
  return <ImageLogo {...props} src={eroadLogo} alt="EROAD" />;
}

export function BlackhawkLogo(props: LogoProps) {
  return <ImageLogo {...props} src={blackhawkLogo} alt="Blackhawk" />;
}

export function NztaLogo(props: LogoProps) {
  // Carjam fronts the NZTA API; no public brand SVG, so the operator
  // asked for a Carjam-blue tile with the wordmark.
  return <Monogram {...props} bg="bg-[#00558C]" text="CARJAM" label="Carjam (NZTA)" />;
}

export function SendgridLogo(props: LogoProps) {
  // SendGrid fronts outbound email; SendGrid brand blue with an EMAIL mark.
  return <Monogram {...props} bg="bg-[#1A82E2]" text="EMAIL" label="Email (SendGrid)" />;
}

export function KudosityLogo(props: LogoProps) {
  // Kudosity (formerly TransmitSMS) fronts outbound SMS.
  return <Monogram {...props} bg="bg-[#6D28D9]" text="SMS" label="SMS (Kudosity)" />;
}

export function SmtpLogo(props: LogoProps) {
  // Generic SMTP relay — slate tile, no brand asset.
  return <Monogram {...props} bg="bg-[#475569]" text="SMTP" label="SMTP server" />;
}
